import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Universal controller for handling real-time comments on any commentable resource
export default class extends Controller {
  static values = { 
    commentableType: String,
    commentableId: Number
  }
  
  connect() {
    console.log(`🔌 Connecting to real-time comments for ${this.commentableTypeValue} ${this.commentableIdValue}`)
    
    this.subscription = consumer.subscriptions.create("CommentsChannel", {
      connected() {
        console.log("📺 Connected to comments channel for real-time updates")
      },

      disconnected() {
        console.log("📺 Disconnected from comments channel")
      },

      received: (data) => {
        // Process real-time updates for this specific commentable
        if (data.commentable_type === this.commentableTypeValue &&
            data.commentable_id === this.commentableIdValue) {
          this.handleCommentUpdate(data)
        }
      }
    })
  }
  
  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      console.log("🔌 Unsubscribed from comments channel")
    }
  }
  
  handleCommentUpdate(data) {
    console.log(`📺 Received comment update:`, data.action)
    
    switch(data.action) {
      case "comments_updated":
        this.handleCommentsUpdate(data)
        break
        
      case "comment_vote_update":
        this.handleVoteUpdate(data)
        break
        
      case "comment_reply_count_update":
        this.handleReplyCountUpdate(data)
        break
        
      case "comment_fresh_score_update_safe":
        this.handleFreshScoreUpdate(data)
        break
        
      case "axle_reply":
        this.handleAxleReply(data)
        break
        
      case "commentable_stats_updated":
        this.handleStatsUpdate(data)
        break
    }
  }
  
  handleCommentsUpdate(data) {
    console.log(`💬 Updating comments for ${data.commentable_type}`)
    
    if (data.turbo_stream) {
      Turbo.renderStreamMessage(data.turbo_stream)
      
      // Add subtle animation to show update
      this.animateCommentsUpdate()
    }
  }
  
  handleVoteUpdate(data) {
    console.log(`❤️ Processing vote update for comment ${data.comment_id}`)
    
    // Don't update for the user who voted (they get immediate local update)
    if (data.voter_id !== this.getCurrentUserId()) {
      if (data.turbo_stream) {
        Turbo.renderStreamMessage(data.turbo_stream)
        
        // Add pulse animation
        this.pulseElement(`comment-vote-${data.comment_id}`, 'vote')
      }
    }
  }
  
  handleReplyCountUpdate(data) {
    console.log(`💬 Updating reply count for comment ${data.comment_id}`)
    
    if (data.turbo_stream) {
      Turbo.renderStreamMessage(data.turbo_stream)
      
      // Bounce animation for reply count
      this.pulseElement(`comment-reply-count-${data.comment_id}`, 'bounce')
    }
  }
  
  handleFreshScoreUpdate(data) {
    console.log(`🔥 Updating fresh score for comment ${data.comment_id}`)
    
    const freshScore = document.querySelector(`#comment-fresh-score-${data.comment_id}`)
    if (freshScore && data.new_score) {
      // Update score value while preserving fire icon
      freshScore.innerHTML = `<i class="fas fa-fire mr-1"></i>${data.new_score}`
      
      // Fire pulse animation
      this.pulseElement(`comment-fresh-score-${data.comment_id}`, 'fire')
    }
  }
  
  handleAxleReply(data) {
    console.log(`🤖 Processing Axle AI reply`)
    
    if (data.turbo_stream) {
      Turbo.renderStreamMessage(data.turbo_stream)
      
      // Add special golden glow for Axle replies
      setTimeout(() => {
        const axleComment = document.querySelector(`#comment-${data.axle_comment_id}`)
        if (axleComment) {
          this.highlightAxleReply(axleComment)
        }
      }, 100)
    }
  }
  
  handleStatsUpdate(data) {
    console.log(`📊 Updating ${data.commentable_type} stats`)
    
    if (data.turbo_stream) {
      Turbo.renderStreamMessage(data.turbo_stream)
    }
  }
  
  // Animation helpers
  animateCommentsUpdate() {
    const commentsContainer = this.element.querySelector('[id^="comments"]')
    if (commentsContainer) {
      commentsContainer.style.animation = 'fade-in 0.3s ease-in-out'
      setTimeout(() => {
        commentsContainer.style.animation = ''
      }, 300)
    }
  }
  
  pulseElement(elementId, type = 'default') {
    const element = document.querySelector(`#${elementId}`)
    if (!element) return
    
    switch(type) {
      case 'vote':
        element.style.animation = 'pulse 0.5s ease-in-out'
        setTimeout(() => element.style.animation = '', 500)
        break
        
      case 'bounce':
        element.style.transform = 'scale(1.1)'
        element.style.transition = 'transform 0.2s ease-out'
        setTimeout(() => {
          element.style.transform = 'scale(1)'
          setTimeout(() => element.style.transition = '', 200)
        }, 200)
        break
        
      case 'fire':
        element.style.animation = 'bounce 0.8s ease-in-out'
        setTimeout(() => element.style.animation = '', 800)
        break
        
      default:
        element.style.animation = 'pulse 0.3s ease-in-out'
        setTimeout(() => element.style.animation = '', 300)
    }
  }
  
  highlightAxleReply(element) {
    // Golden glow effect for AI responses
    element.style.backgroundColor = 'rgba(251, 191, 36, 0.15)'
    element.style.borderLeft = '3px solid #f59e0b'
    element.style.transition = 'all 0.5s ease-out'
    element.style.animation = 'pulse 2s ease-in-out'
    
    setTimeout(() => {
      element.style.backgroundColor = 'rgba(251, 191, 36, 0.05)'
      element.style.borderLeft = '2px solid #f59e0b'
      setTimeout(() => {
        element.style.animation = ''
      }, 2000)
    }, 500)
    
    console.log('🤖✨ Applied golden glow to Axle AI reply')
  }
  
  getCurrentUserId() {
    const userMeta = document.querySelector('meta[name="current-user-id"]')
    return userMeta ? parseInt(userMeta.content) : null
  }
  
  // Thread management helpers
  toggleReply(event) {
    const commentId = event.target.dataset.commentId
    const replyForm = document.querySelector(`#reply-form-${commentId}`)
    
    if (replyForm) {
      replyForm.classList.toggle('hidden')
      
      // Focus on textarea if showing
      if (!replyForm.classList.contains('hidden')) {
        const textarea = replyForm.querySelector('textarea')
        if (textarea) {
          textarea.focus()
        }
      }
    }
  }
  
  cancelReply(event) {
    const form = event.target.closest('[id^="reply-form-"]')
    if (form) {
      form.classList.add('hidden')
      
      // Clear the textarea
      const textarea = form.querySelector('textarea')
      if (textarea) {
        textarea.value = ''
      }
    }
  }
  
  handleReplySubmit(event) {
    console.log('📝 Processing reply submission')
    
    // Add loading state
    const submitButton = event.target.querySelector('[type="submit"]')
    if (submitButton) {
      submitButton.disabled = true
      submitButton.textContent = 'Replying...'
    }
  }
}